import { Component } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Exception } from '../models/exception.model';
import { GridReadyEvent, FilterModel, AdvancedFilterModel, ICellRendererParams, ColDef, GridApi, ValueFormatterParams, IServerSideDatasource, RowModelType, GridOptions, GridPreDestroyedEvent, GetRowIdFunc, GetRowIdParams, IServerSideGetRowsParams, SortModelItem, StateUpdatedEvent, ValueGetterParams } from 'ag-grid-community';
import { IconCellRendererComponent } from '../icon-cell-renderer/icon-cell-renderer.component';
import 'ag-grid-enterprise';
import { LadingDocumentList } from '../models/lading-document-lists.model';
import { storeReportDefaultGridState } from '../default-grid-states/store-report-default-grid-state';
import { UserGridStateService } from '../user-grid-state.service';
import { PalletRacksModalComponent } from '../pallet-racks/pallet-racks-modal/pallet-racks-modal.component';
import { NgZone } from '@angular/core';

@Component({
  selector: 'app-lading-document-lists',
  templateUrl: './lading-document-lists.component.html',
  styleUrls: ['./lading-document-lists.component.scss']
})
export class LadingDocumentListsComponent {
  private apiUrl = '/api/work_orders/lading_document_lists.php';
  public theme: string ="ag-theme-ulaap";
  public rowModelType: RowModelType = 'serverSide';
  public cacheBlockSize = 100;
  public cacheOverflowSize = 2;
  public maxConcurrentDatasourceRequests = 2;
  public infiniteInitialRowCount = 1;
  public initialState = JSON.parse(JSON.stringify(storeReportDefaultGridState));
  public gridReady = false;
  public gridVisible = true;
  public gridOptions: GridOptions = {};
  private gridApi!: GridApi<LadingDocumentList>;
  public rowData?: LadingDocumentList[];
  public defaultColDef: ColDef = {
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      caseSensitive: false,
      buttons: ['apply','clear','reset']
    },
    autoHeight: true,
    resizable: true
  }
  public columnDefs: ColDef[] = [
    { field: 'lading_uuid', headerName: 'UUID', hide: true },
    { field: 'alternate_reference', headerName: 'FBI No.', cellStyle: {'font-weight': 'bold'}},    
    { field: 'customer_name', headerName: 'Customer', width: 200},
    { field: 'load_end', headerName: 'Delivery Date', filter: 'agDateColumnFilter', valueGetter: params => params.data.load_end !== null ? params.data.load_end.slice(0,-3) : '',
    },
    { field: 'document_count', 
      headerName: 'Total Documents', 
      width: 170 
    },    
    { field: 'bol', 
      headerName: 'BOL', 
      width: 120 , 
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === 0 ? '❌' : '✅';
      }      
    },
    { field: 'pod', 
      headerName: 'POD', 
      width: 130 , 
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === 0 ? '❌' : '✅';
      }          
    },
    { field: 'carrier_invoice', 
      headerName: 'Carrier Invoice' , 
      width: 130 ,       
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === 0 ? '❌' : '✅';
      }          
    },
    { field: 'packing_slip', 
      headerName: 'Packing Slip' , 
      width: 130 ,       
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === 0 ? '❌' : '✅';
      }          
    },    
    { field: 'customs_document', 
      headerName: 'Customs Doc' , 
      width: 130 ,       
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === 0 ? '❌' : '✅';
      }          
    },       
    { field: 'in_service', 
      headerName: 'In Service' , 
      width: 130 ,       
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === 0 ? '❌' : '✅';
      }          
    }, 
    { field: 'site_survey', 
      headerName: 'Site Survey' , 
      width: 130 ,       
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === 0 ? '❌' : '✅';
      }          
    }, 
    { field: 'in_agreement', 
      headerName: 'In Agreement' , 
      width: 130 ,       
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === 0 ? '❌' : '✅';
      }          
    }, 
    { field: 'check_list', 
      headerName: 'Check List' , 
      width: 130 ,       
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === 0 ? '❌' : '✅';
      }          
    }, 
    { field: 'disclaimer', 
      headerName: 'Disclaimer' , 
      width: 130 ,       
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === 0 ? '❌' : '✅';
      }          
    },    
    {
      headerName: 'Action', cellRenderer: (params: any) => {
        const button = document.createElement('button');
        const icon = document.createElement('i');

        // Add Font Awesome classes to the icon element
        icon.classList.add('fas', 'fa-eye'); 

        // Append the icon to the button
        button.appendChild(icon);
        button.addEventListener('click', () => {
          this.zone.run(() => {
            this.viewWorkOrder(params);
          });
        });
        return button;
      }, editable: false, width: 100,
    },      
  ]

  constructor(private http: HttpClient, private gridStateService: UserGridStateService, private zone: NgZone){}

  ngOnInit(){
    this.fetchAndApplyGridState();
  }

  // changes column names in filterModel for columns that appear in multiple tables referenced in SQL query
  // also switches date values for bad date ranges (dateTo <--> dateFrom)
  private translateFilters(filterModel: any): any {
    const aliasMapping: {[key: string]: string} = {
      "asn_number": "shipment_list.asn_number",
      "external_bill_of_lading": "shipment_list.external_bill_of_lading",
      "store_number": "shipment_list.store_number",
      "outbound_scan_date": "products.date",
      "first_delivered_scan": "products.delivered_time",
      "last_delivered_scan": "products.delivered_time"
      //"blah_blah": "blahblah.foo_bar",
      //moar mappings
    };

    const translatedFilters: { [key: string]: any} = {};
    for (const alias in filterModel) {
      // switches date values if needed
      if(filterModel[alias].type === 'inRange' && filterModel[alias].filterType === 'date'){
        let startDate = new Date(filterModel[alias].dateFrom);
        let endDate = new Date(filterModel[alias].dateTo);
        if(startDate > endDate){
          const dateHolder = filterModel[alias].dateTo;
          filterModel[alias].dateTo = filterModel[alias].dateFrom;
          filterModel[alias].dateFrom = dateHolder;
        }
      }
      // switch number values if needed
      if(filterModel[alias].type === 'inRange' && filterModel[alias].filterType === 'number' && filterModel[alias].filterTo < filterModel[alias].filter){
        const numHolder = filterModel[alias].filterTo;
        filterModel[alias].filterTo = filterModel[alias].filter;
        filterModel[alias].filter = numHolder;
      }
      // move filterModel data into new filter model with updated alias
      const qualifiedName: string = aliasMapping[alias] || alias;
      translatedFilters[qualifiedName] = filterModel[alias];
    }
    return translatedFilters;
  }


  onGridReady(params: GridReadyEvent<LadingDocumentList>){
    const dataSource: IServerSideDatasource = {
      getRows: (params: IServerSideGetRowsParams<any, any> & { sortModel: SortModelItem[], filterModel: any}) => {
        const sortModel = params.request.sortModel;
        let filterModel : AdvancedFilterModel | null | FilterModel = params.request.filterModel;
        filterModel = this.translateFilters(filterModel); 
        const url = this.buildUrl(params.request.startRow || 0, params.request.endRow || 0, sortModel, filterModel);
        //console.log(`the filterModel that will be sent is: ${JSON.stringify(filterModel)}`);
        

        this.gridApi = params.api;

        this.http.get<{ data: {data: LadingDocumentList[]}, totalRows: number}>(url).subscribe(
          response => {
            if(Array.isArray(response.data.data)){
              params.success({ rowData: response.data.data, rowCount: response.totalRows})
            }
            // else{
            //   console.log('response.data.data is not an array, it is a...' + (typeof response.data))
            //   console.log(JSON.stringify(response))
            // }
          },
          error => {
            console.error('error getting store report data: ', error);
            params.fail();
          }
        )
      }
    }

    params.api.setGridOption('serverSideDatasource', dataSource);
  }

  private buildUrl(startRow: number, endRow: number, sortModel: SortModelItem[], filterModel: any): string {
    // Construct the URL with query parameters for startRow, endRow, filters, and sortModel
    const filters = encodeURIComponent(JSON.stringify(filterModel));
    const sort = encodeURIComponent(JSON.stringify(sortModel));
    return `${this.apiUrl}?startRow=${startRow}&endRow=${endRow}&filters=${filters}&sortModel=${sort}`;
  }

  exportToCSV() {
    var params = { columnSeparator: "|"}
    this.gridApi.exportDataAsCsv(params);
  }

  exportToExcel(){
    this.gridApi.exportDataAsExcel();
  }

  toggleTheme(){
    this.theme = this.theme === 'ag-theme-ulaap' ? 'ag-theme-ulaap-dark' : 'ag-theme-ulaap';
  }

  openDeliveryReports(){
    window.open('/delivery_reports');
  }

  openExceptionReports(){
    window.open('/exception_reports');
  }

  statusTypeFormatting(params: ICellRendererParams) {
    let statusTypes: string[] = []
    if (params.value != null) {
      statusTypes = params.value.split(",");
    }
    let buttonsHtml = '';
  
    for (let type of statusTypes) {
      let trimmedType = type.trim();
      let bgColor = '';

      if(trimmedType.toUpperCase().includes('LATE')){
        bgColor = '#FFF';
      }
      else if(trimmedType.toUpperCase().includes('EARLY')){
        bgColor = '#FF0';
      }
      else if(trimmedType.toUpperCase().includes('ON TIME')){
        bgColor = '#0F0';
      }
  
      buttonsHtml += '<button class="btn dropdown-toggle button-text-wrapper" style="background-color:' + bgColor + ';border-radius:10px;font-weight:bold;">' + trimmedType + '</button> <br>';
    }
  
    let element = '<div class="btn-group">' + buttonsHtml + '</div>';
  
    return element;
  }

  fetchAndApplyGridState(){
    this.gridStateService.getUserGridState('store_reports').subscribe((state) => {
      if(state){
        this.initialState = state;
      }
      this.gridReady = true;
    },
    error => {
      console.error('Failed to fetch grid state:', error);
      this.gridReady = true;
    }
    )
  }

  onStateUpdated(params:StateUpdatedEvent<LadingDocumentList>){
    if(params.sources.indexOf('columnSizing') > -1 || params.sources.indexOf('columnOrder') > -1){
      this.gridStateService.saveUserGridState('store_reports', params.state)
    }
  }

  viewWorkOrder(params: any) {
    const lading_uuid = params.data.lading_uuid;

    window.open('/full-order-details/' + lading_uuid, '_blank');
  }

  refresh(params: any): boolean {
    return false;
  }  
}
